import { send } from './client';

/**
 * The reference hierarchy — dispensary -> city -> centre — and the CSV import
 * that replaces the legacy `uploadCenter.php` (M-03).
 *
 * A separate module from `client.ts`, mirroring `users.ts`: a different
 * audience (`reference.manage`, not `user.*`) and a different lifetime. Every
 * call goes through `send`, so it inherits the CSRF handshake, the one-shot
 * 419 retry, `credentials: 'include'` and the single `ApiError` envelope —
 * there is no second fetch path in this application.
 *
 * ## `send<T>()`, not `request<T>()`
 *
 * Every read below is `send<T>()`. `GET /reference/dispensaries|cities|centres`
 * answers a BARE LIST with no `meta` at all — confirmed against the API
 * routes' own docblock: "UNPAGINATED, DELIBERATELY... the hierarchy is
 * bounded" (unlike `GET /audit-log`, which is cursor-paginated and carries
 * `meta.next_cursor` — `client.ts`'s `auditLog()` composes `request()`'s
 * `data`+`meta` explicitly for exactly that reason, and would resolve to
 * `undefined.data` here: there is no `meta` on this response to read at all).
 * `send()` discarding `meta` is therefore the CORRECT choice for this file,
 * not an oversight — the opposite finding from M-02b's Task 6, which is why
 * it is written down here rather than assumed silently.
 *
 * ## `dispensary_uuid`/`city_uuid` do not round-trip as `dispensary_id`/`city_id`
 *
 * `CentreResource` publishes a centre's parents' UUIDs under `..._uuid` (and,
 * since cities became global, the parents themselves under `dispensary`/
 * `city` with both names). Every WRITE request instead reads
 * `dispensary_id`/`city_id` — despite the name, these are the parent's UUID
 * too, never the integer primary key (`CentreRequestBase`'s own docblock:
 * "this API addresses every resource by uuid externally"). The two field
 * names differ on the wire in the two directions, and nothing here pretends
 * otherwise: `CentreInput` names the fields `dispensary_id`/`city_id` to
 * match what the server actually reads, while `Centre` names them
 * `dispensary_uuid`/`city_uuid` to match what the server actually returns.
 */

/** The four fields every level of the hierarchy shares. */
export interface ReferenceNode {
  uuid: string;
  external_code: string;
  name_en: string;
  name_ar: string;
  is_active: boolean;
}

export type Dispensary = ReferenceNode;

/**
 * A city is GLOBAL — one row per real place, no parent (the API's
 * `2026_10_21_000100_make_cities_global` migration). It used to carry a
 * `dispensary_uuid`, because the old schema stored "Riyadh" once per
 * dispensary that served it; the product owner found the resulting list
 * (288 rows, 98 names) unusable. Which dispensaries serve a city is now
 * DERIVED from centres — ask with `citiesApi.list(false, dispensaryUuid)`.
 */
export interface City extends ReferenceNode {
  /** Live (not soft-deleted) centres in this city, across every dispensary.
   *  Present on `GET /reference/cities` rows; absent on a single create/edit
   *  response, which is why it is optional. */
  centres_count?: number;
}

/** A parent as a centre row carries it: enough to render without a lookup. */
export interface ReferenceParentRef {
  uuid: string;
  name_en: string;
  name_ar: string;
}

export interface Centre extends ReferenceNode {
  dispensary_uuid: string | null;
  city_uuid: string | null;
  /** The owning dispensary, names included, so a list can show which
   *  dispensary a centre belongs to without fetching the dispensary
   *  catalogue first. `null` exactly when `dispensary_uuid` is (a
   *  soft-deleted parent — see the API's `CentreResource`). */
  dispensary: ReferenceParentRef | null;
  /** The centre's city — same shape and same `null` rule as `dispensary`. */
  city: ReferenceParentRef | null;
  place: string | null;
  directions: string | null;
  google_map_url: string | null;
}

export interface DispensaryInput {
  external_code: string;
  name_en: string;
  name_ar: string;
  is_active: boolean;
}

/** No parent field: a city belongs to no dispensary (see `City`). */
export interface CityInput {
  external_code: string;
  name_en: string;
  name_ar: string;
  is_active: boolean;
}

export interface CentreInput {
  /** The owning dispensary's UUID — see the module docblock. Chosen
   *  explicitly now that a city no longer implies one. */
  dispensary_id: string;
  /** The city's UUID — see the module docblock. */
  city_id: string;
  external_code: string;
  name_en: string;
  name_ar: string;
  place: string | null;
  directions: string | null;
  google_map_url: string | null;
  is_active: boolean;
}

/** `GET /reference/centres` filters — both UUIDs, both optional, combined with AND. */
export interface CentreFilter {
  dispensary?: string;
  city?: string;
}

/**
 * `?include_inactive=true&...`, or `''` when every value is absent.
 * `include_inactive` is sent as the literal string `'true'` — the server
 * reads it with Laravel's `Request::boolean()`, which accepts `1`/`true`/
 * `on`/`yes` and treats everything else (including an absent parameter) as
 * false (`ReferenceIndexRequest::includeInactive()`'s own docblock).
 */
function query(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) search.set(key, value);
  }
  const built = search.toString();
  return built === '' ? '' : `?${built}`;
}

export const dispensariesApi = {
  /** Any authenticated, active account — no `reference.manage` needed to read. */
  list: (includeInactive = false) =>
    send<readonly Dispensary[]>(
      `/reference/dispensaries${query({ include_inactive: includeInactive ? 'true' : undefined })}`,
    ),

  create: (input: DispensaryInput) =>
    send<Dispensary>('/reference/dispensaries', { method: 'POST', body: input }),

  update: (uuid: string, input: Partial<DispensaryInput>) =>
    send<Dispensary>(`/reference/dispensaries/${uuid}`, { method: 'PATCH', body: input }),

  activate: (uuid: string) =>
    send<Dispensary>(`/reference/dispensaries/${uuid}/activate`, { method: 'POST' }),

  deactivate: (uuid: string) =>
    send<Dispensary>(`/reference/dispensaries/${uuid}/deactivate`, { method: 'POST' }),

  remove: (uuid: string) => send<void>(`/reference/dispensaries/${uuid}`, { method: 'DELETE' }),
};

export const citiesApi = {
  /**
   * `dispensaryUuid` narrows to the cities that dispensary SERVES — those
   * holding at least one of its active centres (`CityIndexRequest`). This is
   * what the case form cascades through: dispensary -> its cities -> the
   * centres of that dispensary in that city.
   */
  list: (includeInactive = false, dispensaryUuid?: string) =>
    send<readonly City[]>(
      `/reference/cities${query({
        include_inactive: includeInactive ? 'true' : undefined,
        dispensary: dispensaryUuid,
      })}`,
    ),

  create: (input: CityInput) => send<City>('/reference/cities', { method: 'POST', body: input }),

  update: (uuid: string, input: Partial<CityInput>) =>
    send<City>(`/reference/cities/${uuid}`, { method: 'PATCH', body: input }),

  activate: (uuid: string) => send<City>(`/reference/cities/${uuid}/activate`, { method: 'POST' }),

  deactivate: (uuid: string) => send<City>(`/reference/cities/${uuid}/deactivate`, { method: 'POST' }),

  remove: (uuid: string) => send<void>(`/reference/cities/${uuid}`, { method: 'DELETE' }),
};

export const centresApi = {
  /** `dispensary`/`city`, UUIDs — never integer ids (`CentreIndexRequest`'s own docblock). */
  list: (includeInactive = false, filter: CentreFilter = {}) =>
    send<readonly Centre[]>(
      `/reference/centres${query({
        include_inactive: includeInactive ? 'true' : undefined,
        dispensary: filter.dispensary,
        city: filter.city,
      })}`,
    ),

  create: (input: CentreInput) => send<Centre>('/reference/centres', { method: 'POST', body: input }),

  update: (uuid: string, input: Partial<CentreInput>) =>
    send<Centre>(`/reference/centres/${uuid}`, { method: 'PATCH', body: input }),

  activate: (uuid: string) => send<Centre>(`/reference/centres/${uuid}/activate`, { method: 'POST' }),

  deactivate: (uuid: string) =>
    send<Centre>(`/reference/centres/${uuid}/deactivate`, { method: 'POST' }),

  remove: (uuid: string) => send<void>(`/reference/centres/${uuid}`, { method: 'DELETE' }),
};

/** One row of a CSV import's per-row verdict — the HTTP-facing subset
 *  `CentreController::presentImport()` publishes. `centre_id`, the resolved
 *  parent ids and `attributes` are write-time detail the API deliberately
 *  never puts on the wire. */
export interface ImportRow {
  row_number: number;
  external_code: string | null;
  verdict: 'create' | 'update' | 'unchanged' | 'reject';
  /** Plain, untranslated diagnostic text on a `reject` row — deliberately not
   *  run through the server's translation layer (`CentreImport`'s own
   *  docblock: "has no fixed vocabulary a `lang/*` file could enumerate").
   *  Rendered as-is, the same way a spreadsheet's own validation would be. */
  reason: string | null;
  /** The field names `CentreImport::diffRow()` found changed — `null` on
   *  every verdict but `update`. This is the fix for the bulk-rename bug: a
   *  legal five-column CSV must not silently null `place`, `directions` and
   *  `google_map_url`, and this array is how a preview proves it didn't.
   *  The server computes it; this screen only ever displays it verbatim. */
  changes: readonly string[] | null;
}

export interface ImportResult {
  /** A hash of the PARSED file content. Opaque — carried back verbatim as
   *  `preview_token` on the apply call and never inspected here. */
  preview_token: string;
  /** `false` on the dry run, `true` once this response reflects an apply
   *  that actually wrote rows. */
  applied: boolean;
  counts: { create: number; update: number; unchanged: number; reject: number };
  rows: readonly ImportRow[];
}

export const centresImportApi = {
  /**
   * The dry run — no `preview_token` sent. Writes nothing
   * (`CentreController::import()`'s own docblock): the whole point is a
   * response an administrator can read before anything happens.
   */
  preview: (file: string) =>
    send<ImportResult>('/reference/centres/import', { method: 'POST', body: { file } }),

  /**
   * The apply — the SAME file, re-sent, plus the token the dry run returned.
   * A token that does not match the re-parsed file is a 422 naming
   * `preview_token` (`CentreController::import()`: "re-deriving the row list
   * from the file is what makes the token comparison ... a genuine proof
   * that the file being applied is the file that was previewed").
   */
  apply: (file: string, previewToken: string) =>
    send<ImportResult>('/reference/centres/import', {
      method: 'POST',
      body: { file, preview_token: previewToken },
    }),
};
